import type { FilteredListingControlsSnapshot } from './useFilteredListingControls';
import type { ListingUrlAdapter } from './useListingUrlState';

/** Parse a `location.search` string into URLSearchParams, tolerating a leading `?`. */
export function parseListingSearch(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
}

/** Remove only the listed query param keys (safe allowlist strip). */
export function stripListingKeys(
  params: URLSearchParams,
  keys: readonly string[],
): void {
  keys.forEach((key) => params.delete(key));
}

type ListingStringFilterField<TFilters> = {
  /** Browser URL query param key (e.g. `department`). */
  param: string;
  /** Key in the filters object this param maps to. */
  key: keyof TFilters & string;
  /** Trim the value before writing it / deciding whether to write it. */
  trim?: boolean;
  /** Legacy prefixed key to read when `param` is absent (one-release migration). */
  legacyParam?: string;
};

type CreateListingUrlAdapterOptions<TFilters extends Record<string, string>> = {
  /**
   * Optional legacy prefix for migration shims only (e.g. `jobs_` reads `jobs_status`
   * when `status` is absent). New writes always use bare `param` keys.
   */
  legacyPrefix?: string;
  /** Default (empty) filters, used to backfill keys when hydrating. */
  defaultFilters: TFilters;
  /** String filter fields handled by this adapter. */
  fields?: ListingStringFilterField<TFilters>[];
  /** Browser URL key for search text. Defaults to `search`. */
  qParam?: string;
  /** When provided, the adapter also syncs the `sort` field. */
  sort?: {
    /** Browser URL key. Defaults to `sort`. */
    param?: string;
    /** Sort value that should be treated as default and never written. */
    skipValue?: string;
  };
};

function resolveParamKey(param: string): string {
  return param;
}

function legacyParamKey(
  param: string,
  legacyPrefix: string | undefined,
): string | undefined {
  return legacyPrefix ? `${legacyPrefix}${param}` : undefined;
}

/**
 * Builds a {@link ListingUrlAdapter} for the common case where a listing's
 * filters are a flat record of string values. Uses an allowlist of managed keys
 * so unrelated query params (modals, deep links) are preserved.
 */
export function createListingUrlAdapter<
  TFilters extends Record<string, string>,
>(
  options: CreateListingUrlAdapterOptions<TFilters>,
): ListingUrlAdapter<FilteredListingControlsSnapshot<TFilters>> {
  const {
    legacyPrefix,
    defaultFilters,
    fields = [],
    qParam = 'search',
    sort,
  } = options;
  type Snapshot = FilteredListingControlsSnapshot<TFilters>;
  const sortKey = sort?.param ?? 'sort';
  const legacyQKey = legacyPrefix ? `${legacyPrefix}q` : undefined;

  const managedKeys = [
    qParam,
    ...(sort ? [sortKey] : []),
    ...fields.map(({ param }) => param),
    ...(legacyPrefix
      ? [
          `${legacyPrefix}q`,
          ...(sort ? [`${legacyPrefix}${sortKey}`] : []),
          ...fields.map(({ param }) => `${legacyPrefix}${param}`),
        ]
      : []),
  ];

  return {
    hydrateFromSearch: (search) => {
      const params = parseListingSearch(search);
      const partial: Partial<Snapshot> = {};

      const q =
        params.get(qParam) ?? (legacyQKey ? params.get(legacyQKey) : null);
      if (q != null) partial.q = q;

      if (sort) {
        const sortValue =
          params.get(sortKey) ??
          (legacyPrefix ? params.get(`${legacyPrefix}${sortKey}`) : null);
        if (sortValue) partial.sort = sortValue;
      }

      const filtersPartial: Partial<TFilters> = {};
      fields.forEach(({ param, key, legacyParam }) => {
        const fullKey = resolveParamKey(param);
        const legacyKey = legacyParam ?? legacyParamKey(param, legacyPrefix);
        if (params.has(fullKey)) {
          filtersPartial[key] = (params.get(fullKey) ??
            '') as TFilters[typeof key];
        } else if (legacyKey && params.has(legacyKey)) {
          filtersPartial[key] = (params.get(legacyKey) ??
            '') as TFilters[typeof key];
        }
      });
      if (Object.keys(filtersPartial).length > 0) {
        partial.filters = { ...defaultFilters, ...filtersPartial };
      }

      return partial;
    },
    buildSearch: (existingSearch, snapshot) => {
      const params = parseListingSearch(existingSearch);
      stripListingKeys(params, managedKeys);

      if (snapshot.q) params.set(qParam, snapshot.q);

      if (sort && snapshot.sort && snapshot.sort !== sort.skipValue) {
        params.set(sortKey, snapshot.sort);
      }

      fields.forEach(({ param, key, trim }) => {
        const raw = snapshot.filters[key] ?? '';
        const value = trim ? raw.trim() : raw;
        if (value) params.set(param, value);
      });

      return params.toString();
    },
  };
}

type CreateListingTabUrlAdapterOptions = {
  /** Full query param key (e.g. `tab`). */
  paramKey: string;
  /** Legacy key to read when `paramKey` is absent (one-release migration). */
  legacyParamKey?: string;
  getDefault: () => string;
  /** Value written when snapshot tab is empty (e.g. admin default). */
  getFallbackWrite?: () => string | undefined;
};

/** Adapter for a single top-level string field on a listing snapshot (e.g. Jobs tab). */
export function createListingTabUrlAdapter(
  options: CreateListingTabUrlAdapterOptions,
): ListingUrlAdapter<{ tab: string }> {
  const { paramKey, legacyParamKey, getDefault, getFallbackWrite } = options;

  return {
    hydrateFromSearch: (search) => {
      const params = parseListingSearch(search);
      const tabParam =
        params.get(paramKey) ??
        (legacyParamKey ? params.get(legacyParamKey) : null);
      return { tab: tabParam ?? getDefault() };
    },
    buildSearch: (existingSearch, snapshot) => {
      const params = parseListingSearch(existingSearch);
      params.delete(paramKey);
      if (legacyParamKey) params.delete(legacyParamKey);

      if (snapshot.tab) {
        params.set(paramKey, snapshot.tab);
      } else {
        const fallback = getFallbackWrite?.();
        if (fallback) params.set(paramKey, fallback);
      }

      return params.toString();
    },
  };
}

/** Compose multiple listing URL adapters (e.g. filters + tab). */
export function mergeListingUrlAdapters<T>(
  ...adapters: ListingUrlAdapter<Partial<T>>[]
): ListingUrlAdapter<T> {
  return {
    hydrateFromSearch: (search) =>
      adapters.reduce<Partial<T>>(
        (acc, adapter) => ({ ...acc, ...adapter.hydrateFromSearch(search) }),
        {},
      ),
    buildSearch: (existingSearch, snapshot) =>
      adapters.reduce(
        (search, adapter) =>
          adapter.buildSearch(search, snapshot as Partial<T>),
        existingSearch,
      ),
  };
}

/**
 * Syncs the listing `page` field to the browser URL (`?page=N`).
 * Page 1 is omitted from the URL (default).
 */
export function createPageUrlAdapter(
  param = 'page',
): ListingUrlAdapter<{ page: number }> {
  return {
    hydrateFromSearch: (search) => {
      const params = parseListingSearch(search);
      const pageRaw = params.get(param);
      if (!pageRaw) return {};
      const page = Number.parseInt(pageRaw, 10);
      return page >= 1 ? { page } : {};
    },
    buildSearch: (existingSearch, snapshot) => {
      const params = parseListingSearch(existingSearch);
      params.delete(param);
      if (snapshot.page > 1) {
        params.set(param, String(snapshot.page));
      }
      return params.toString();
    },
  };
}
